import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import WorkFields from "../FormComponents/WorkFields";
import {
  fetchWorkDetailsByParent,
  addWorkData,
  editWorkData,
  deleteWorkData,
} from "../../services/workService";

/**
 * Add Work Experience Dialog Component
 */
export const AddWorkExperienceDialog = ({
  open,
  onClose,
  sessionId,
  userId,
  onSuccess,
  onError,
  onWorkAdded,
}) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleClear = () => {
    setFormData({
      companyname1: "",
      sector1: "",
      department1: "",
      designation1: "",
      employmenttype1: "",
      description1: "",
      startdate1: "",
      currentlyworking1: false,
      enddate1: "",
      address1: "",
      country1: "",
      state1: "",
      city1: "",
  pincode1: "",
  workimage1: "",
    });
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const workData = {
        companyname: formData.companyname1,
        sector: formData.sector1,
        department: formData.department1,
        designation: formData.designation1,
        employmenttype: formData.employmenttype1,
        description: formData.description1,
        startdate: formData.startdate1,
        currentlyworking: formData.currentlyworking1 ? "1" : "0",
        enddate: formData.currentlyworking1 ? "" : formData.enddate1,
        address: formData.address1,
        country: formData.country1,
        state: formData.state1,
        city: formData.city1,
        pincode: formData.pincode1,
  image: formData.workimage1,
        parentid: userId,
      };

      await addWorkData(sessionId, workData);

      // Fetch updated list
      const updatedWorkEntries = await fetchWorkDetailsByParent(sessionId, userId);
      
      if (onWorkAdded) {
        onWorkAdded(updatedWorkEntries);
      }

      handleClear();
      onClose();
      
      if (onSuccess) {
        onSuccess("Work experience added successfully!");
      }
    } catch (error) {
      if (onError) {
        onError("Failed to add work experience. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      handleClear();
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDBox display="flex" alignItems="center">
            <Icon color="info" sx={{ mr: 1 }}>
              work
            </Icon>
            <MDTypography variant="h6" fontWeight="bold">
              Add New Work Experience
            </MDTypography>
          </MDBox>
          <IconButton size="small" onClick={handleDialogClose} disabled={isLoading}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDBox mt={2}>
          <WorkFields
            formData={{
              companyname1: formData.companyname1 || "",
              sector1: formData.sector1 || "",
              department1: formData.department1 || "",
              designation1: formData.designation1 || "",
              employmenttype1: formData.employmenttype1 || "",
              description1: formData.description1 || "",
              startdate1: formData.startdate1 || "",
              currentlyworking1: formData.currentlyworking1 || false,
              enddate1: formData.enddate1 || "",
              address1: formData.address1 || "",
              country1: formData.country1 || "",
              state1: formData.state1 || "",
              city1: formData.city1 || "",
              pincode1: formData.pincode1 || "",
              workimage1: formData.workimage1 || "",
            }}
            handleChange={handleChange}
            section={1}
            textInputStyle={{}}
            fieldErrors={{}}
          />
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton
          onClick={handleClear}
          variant="outlined"
          color="warning"
          disabled={isLoading}
        >
          Clear
        </MDButton>
        <MDButton
          onClick={handleDialogClose}
          variant="outlined"
          color="dark"
          disabled={isLoading}
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={handleSave}
          variant="gradient"
          color="info"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Adding...
            </>
          ) : (
            "Add Work Experience"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Edit Work Experience Dialog Component
 */
export const EditWorkExperienceDialog = ({
  open,
  onClose,
  workEntry,
  sessionId,
  userId,
  onSuccess,
  onError,
  onWorkUpdated,
}) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Load work experience data when dialog opens
  React.useEffect(() => {
    if (open && workEntry) {
      setFormData({
        companyname1: workEntry.companyname || "",
        sector1: workEntry.sector || "",
        department1: workEntry.department || "",
        designation1: workEntry.designation || "",
        employmenttype1: workEntry.employmenttype || "",
        description1: workEntry.description || "",
        startdate1: workEntry.startdate || "",
        currentlyworking1: workEntry.currentlyworking === "1" || workEntry.currentlyworking === true,
        enddate1: workEntry.enddate || "",
        address1: workEntry.address || "",
        country1: workEntry.country || "",
        state1: workEntry.state || "",
        city1: workEntry.city || "",
  pincode1: workEntry.pincode || "",
  workimage1: workEntry.image || "",
      });
    }
  }, [open, workEntry]);

  const handleClear = () => {
    setFormData({
      companyname1: "",
      sector1: "",
      department1: "",
      designation1: "",
      employmenttype1: "",
      description1: "",
      startdate1: "",
      currentlyworking1: false,
      enddate1: "",
      address1: "",
      country1: "",
      state1: "",
      city1: "",
  pincode1: "",
  workimage1: "",
    });
  };

  const handleSave = async () => {
    if (!workEntry || !workEntry.id) return;

    setIsLoading(true);
    try {
      const workData = {
        companyname: formData.companyname1,
        sector: formData.sector1,
        department: formData.department1,
        designation: formData.designation1,
        employmenttype: formData.employmenttype1,
        description: formData.description1,
        startdate: formData.startdate1,
        currentlyworking: formData.currentlyworking1 ? "1" : "0",
        enddate: formData.currentlyworking1 ? "" : formData.enddate1,
        address: formData.address1,
        country: formData.country1,
        state: formData.state1,
        city: formData.city1,
        pincode: formData.pincode1,
  image: formData.workimage1,
        parentid: userId,
      };

      await editWorkData(sessionId, workEntry.id, workData);

      // Fetch updated list
      const updatedWorkEntries = await fetchWorkDetailsByParent(sessionId, userId);
      
      if (onWorkUpdated) {
        onWorkUpdated(updatedWorkEntries);
      }

      onClose();
      
      if (onSuccess) {
        onSuccess("Work experience updated successfully!");
      }
    } catch (error) {
      if (onError) {
        onError("Failed to update work experience. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDBox display="flex" alignItems="center">
            <Icon color="primary" sx={{ mr: 1 }}>
              edit
            </Icon>
            <MDTypography variant="h6" fontWeight="bold">
              Edit Work Experience
            </MDTypography>
          </MDBox>
          <IconButton size="small" onClick={handleDialogClose} disabled={isLoading}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDBox mt={2}>
          <WorkFields
            formData={{
              companyname1: formData.companyname1 || "",
              sector1: formData.sector1 || "",
              department1: formData.department1 || "",
              designation1: formData.designation1 || "",
              employmenttype1: formData.employmenttype1 || "",
              description1: formData.description1 || "",
              startdate1: formData.startdate1 || "",
              currentlyworking1: formData.currentlyworking1 || false,
              enddate1: formData.enddate1 || "",
              address1: formData.address1 || "",
              country1: formData.country1 || "",
              state1: formData.state1 || "",
              city1: formData.city1 || "",
              pincode1: formData.pincode1 || "",
              workimage1: formData.workimage1 || "",
            }}
            handleChange={handleChange}
            section={1}
            textInputStyle={{}}
            fieldErrors={{}}
          />
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton
          onClick={handleClear}
          variant="outlined"
          color="warning"
          disabled={isLoading}
        >
          Clear
        </MDButton>
        <MDButton
          onClick={handleDialogClose}
          variant="outlined"
          color="dark"
          disabled={isLoading}
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={handleSave}
          variant="gradient"
          color="info"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Updating...
            </>
          ) : (
            "Update"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Delete Work Experience Confirmation Dialog Component
 */
export const DeleteWorkExperienceDialog = ({
  open,
  onClose,
  workEntry,
  sessionId,
  userId,
  onSuccess,
  onError,
  onWorkDeleted,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    if (!workEntry || !workEntry.id) {
      onClose();
      return;
    }

    setIsLoading(true);
    try {
      await deleteWorkData(sessionId, workEntry.id);

      // Fetch updated list
      const updatedWorkEntries = await fetchWorkDetailsByParent(sessionId, userId);
      
      if (onWorkDeleted) {
        onWorkDeleted(updatedWorkEntries);
      }

      onClose();
      
      if (onSuccess) {
        onSuccess("Work experience deleted successfully!");
      }
    } catch (error) {
      if (onError) {
        onError("Failed to delete work experience. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center">
          <Icon color="error" sx={{ mr: 1 }}>
            warning
          </Icon>
          <MDTypography variant="h6" fontWeight="bold">
            Confirm Deletion
          </MDTypography>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDTypography variant="body2" color="text">
          Are you sure you want to delete this work experience?
        </MDTypography>
        {workEntry && (
          <MDBox
            mt={2}
            p={2}
            sx={{ backgroundColor: "grey.100", borderRadius: 1 }}
          >
            <MDTypography variant="body2" fontWeight="medium">
              Company: {workEntry.companyname || workEntry.name || "N/A"}
            </MDTypography>
            <MDTypography variant="body2">
              Designation: {workEntry.designation || "N/A"}
            </MDTypography>
            <MDTypography variant="body2">
              Period: {workEntry.startdate || "N/A"} - {workEntry.currentlyworking === "1" ? "Present" : (workEntry.enddate || "N/A")}
            </MDTypography>
          </MDBox>
        )}
        <MDTypography variant="caption" color="error" mt={2} display="block">
          This action cannot be undone.
        </MDTypography>
      </DialogContent>
      <DialogActions>
        <MDButton
          onClick={handleDialogClose}
          variant="outlined"
          color="dark"
          disabled={isLoading}
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={handleConfirm}
          variant="gradient"
          color="error"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Deleting...
            </>
          ) : (
            "Delete"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};
